<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import Spinner from 'next/spinner/Spinner.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import PaginationFooter from 'next/pagination/PaginationFooter.vue';

const props = defineProps({
  botId: { type: Number, required: true },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');

const conversationPath = displayId => {
  if (!displayId) return '';
  const route = router.resolve({
    name: 'inbox_conversation',
    params: {
      accountId: accountId.value,
      conversation_id: displayId,
    },
  });
  return route.href;
};

const criticalOnly = ref(false);
const expandedId = ref(null);

const results = computed(() => store.getters['wgptBotEvalResults/getResults']);
const meta = computed(() => store.getters['wgptBotEvalResults/getMeta']);
const uiFlags = computed(() => store.getters['wgptBotEvalResults/getUIFlags']);
const currentPage = computed(() => meta.value.currentPage || 1);

const fetchResults = (page = 1) => {
  store.dispatch('wgptBotEvalResults/get', {
    botId: props.botId,
    page,
    criticalOnly: criticalOnly.value,
  });
};

const toggleCriticalFilter = () => {
  criticalOnly.value = !criticalOnly.value;
  fetchResults(1);
};

const toggleExpand = async result => {
  if (expandedId.value === result.id) {
    expandedId.value = null;
    return;
  }
  expandedId.value = result.id;
  if (!result.scores?.length) {
    await store.dispatch('wgptBotEvalResults/show', {
      botId: props.botId,
      id: result.id,
    });
  }
};

const getResult = id => {
  return store.getters['wgptBotEvalResults/getResult'](id);
};

const scoreColor = score => {
  if (score >= 70) return 'text-n-green-11';
  if (score >= 50) return 'text-n-amber-11';
  return 'text-n-ruby-11';
};

const scoreBorderColor = score => {
  if (score >= 70) return 'border-n-teal-6';
  if (score >= 50) return 'border-n-amber-6';
  return 'border-n-ruby-6';
};

onMounted(() => {
  fetchResults();
});
</script>

<template>
  <div class="flex flex-col gap-4 pb-8">
    <p class="text-sm text-n-slate-11">
      {{ t('WGPT_BOTS.EDIT.TABS.QUALITY.EVALUATIONS.DESCRIPTION') }}
    </p>

    <div class="flex items-center gap-3">
      <Button
        :label="t('WGPT_BOTS.EDIT.TABS.QUALITY.EVALUATIONS.FILTER_CRITICAL')"
        :variant="criticalOnly ? 'solid' : 'faded'"
        :color="criticalOnly ? 'ruby' : 'slate'"
        size="sm"
        icon="i-lucide-alert-triangle"
        @click="toggleCriticalFilter"
      />
    </div>

    <div
      v-if="uiFlags.isFetching"
      class="flex items-center justify-center py-16"
    >
      <Spinner />
    </div>

    <template v-else>
      <div
        v-if="!results.length"
        class="flex flex-col items-center justify-center py-16 gap-2"
      >
        <Icon icon="i-lucide-clipboard-list" class="size-8 text-n-slate-9" />
        <p class="text-sm text-n-slate-11">
          {{ t('WGPT_BOTS.EDIT.TABS.QUALITY.EVALUATIONS.EMPTY') }}
        </p>
      </div>

      <template v-else>
        <div class="rounded-xl border border-n-weak overflow-hidden">
          <table class="min-w-full">
            <thead>
              <tr class="border-b border-n-weak">
                <th
                  class="py-2.5 px-4 text-start text-xs font-medium text-n-slate-11"
                >
                  {{
                    t(
                      'WGPT_BOTS.EDIT.TABS.QUALITY.EVALUATIONS.COL_CONVERSATION'
                    )
                  }}
                </th>
                <th
                  class="py-2.5 px-4 text-start text-xs font-medium text-n-slate-11"
                >
                  {{ t('WGPT_BOTS.EDIT.TABS.QUALITY.EVALUATIONS.COL_SCORE') }}
                </th>
                <th
                  class="py-2.5 px-4 text-start text-xs font-medium text-n-slate-11"
                >
                  {{ t('WGPT_BOTS.EDIT.TABS.QUALITY.EVALUATIONS.COL_DATE') }}
                </th>
                <th class="py-2.5 px-4 w-10" />
              </tr>
            </thead>
            <tbody>
              <template v-for="result in results" :key="result.id">
                <tr
                  class="border-b border-n-weak last:border-0 cursor-pointer hover:bg-n-alpha-1 transition-colors"
                  :class="{ 'bg-n-alpha-1': expandedId === result.id }"
                  @click="toggleExpand(result)"
                >
                  <td class="py-2.5 px-4">
                    <div class="flex items-center gap-2">
                      <span
                        v-if="result.has_critical_issues"
                        class="inline-block w-2 h-2 rounded-full bg-n-ruby-11 flex-shrink-0"
                      />
                      <a
                        v-if="result.conversation_display_id"
                        :href="conversationPath(result.conversation_display_id)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-sm text-n-brand hover:underline"
                        @click.stop
                      >
                        {{
                          t(
                            'WGPT_BOTS.EDIT.TABS.QUALITY.EVALUATIONS.CONVERSATION_ID',
                            { id: result.conversation_display_id }
                          )
                        }}
                      </a>
                      <span v-else class="text-sm text-n-slate-11">
                        {{
                          t(
                            'WGPT_BOTS.EDIT.TABS.QUALITY.EVALUATIONS.CONVERSATION_DELETED'
                          )
                        }}
                      </span>
                    </div>
                  </td>
                  <td class="py-2.5 px-4">
                    <span
                      class="text-sm font-semibold"
                      :class="scoreColor(result.overall_score)"
                    >
                      {{
                        t(
                          'WGPT_BOTS.EDIT.TABS.QUALITY.EVALUATIONS.SCORE_VALUE',
                          { score: result.overall_score }
                        )
                      }}
                    </span>
                  </td>
                  <td class="py-2.5 px-4 text-sm text-n-slate-11">
                    {{
                      dynamicTime(new Date(result.created_at).getTime() / 1000)
                    }}
                  </td>
                  <td class="py-2.5 px-4">
                    <Icon
                      icon="i-lucide-chevron-right"
                      class="size-3.5 text-n-slate-10 transition-transform duration-200"
                      :class="{ 'rotate-90': expandedId === result.id }"
                    />
                  </td>
                </tr>

                <tr v-if="expandedId === result.id">
                  <td colspan="4" class="p-0">
                    <div
                      v-if="uiFlags.isFetchingItem"
                      class="flex justify-center py-6"
                    >
                      <Spinner />
                    </div>
                    <div v-else class="px-4 py-3 border-b border-n-weak">
                      <p
                        v-if="getResult(result.id).summary"
                        class="text-sm text-n-slate-11 mb-3"
                      >
                        {{ getResult(result.id).summary }}
                      </p>

                      <div class="flex flex-col gap-2">
                        <div
                          v-for="score in getResult(result.id).scores || []"
                          :key="score.id"
                          class="rounded-lg border p-3"
                          :class="scoreBorderColor(score.score)"
                        >
                          <div class="flex items-center justify-between mb-1.5">
                            <div class="flex items-center gap-2">
                              <span
                                v-if="score.is_critical_issue"
                                class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-n-ruby-11 text-white"
                              >
                                {{
                                  t(
                                    'WGPT_BOTS.EDIT.TABS.QUALITY.EVALUATIONS.CRITICAL'
                                  )
                                }}
                              </span>
                              <span class="text-sm font-medium text-n-slate-12">
                                {{ score.criterion_name }}
                              </span>
                            </div>
                            <span
                              class="text-sm font-semibold"
                              :class="scoreColor(score.score)"
                            >
                              {{
                                t(
                                  'WGPT_BOTS.EDIT.TABS.QUALITY.EVALUATIONS.SCORE_VALUE',
                                  { score: score.score }
                                )
                              }}
                            </span>
                          </div>
                          <p
                            v-if="score.reasoning"
                            class="text-xs text-n-slate-11 mb-1"
                          >
                            {{ score.reasoning }}
                          </p>
                          <p
                            v-if="score.evidence"
                            class="text-xs text-n-slate-10 italic"
                          >
                            {{
                              t(
                                'WGPT_BOTS.EDIT.TABS.QUALITY.EVALUATIONS.EVIDENCE_QUOTE',
                                { evidence: score.evidence }
                              )
                            }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div v-if="meta.totalPages > 1" class="relative">
          <PaginationFooter
            :current-page="currentPage"
            :total-items="meta.totalCount"
            :items-per-page="15"
            @update:current-page="fetchResults"
          />
        </div>
      </template>
    </template>
  </div>
</template>
